import { ProfileContext } from 'application/adapters/context/Profile';
import type { Audiences } from 'application/repositories/AudiencesRepository';
import { useContext } from 'react';

/**
 * Audience names come from two systems with different conventions - Ortto uses ASCII
 * transliterations such as Koebenhavn while the CMS may carry Danish characters - so names
 * are always compared case-insensitively and trimmed.
 */
const userHasAudience = (audienceName: string | undefined, audiences: Audiences): boolean => {
	if (!audienceName) return false;

	const normalised = audienceName.toLowerCase().trim();
	return !!audiences?.audiences?.some((audience) => audience?.name?.toLowerCase().trim() === normalised);
};

/**
 * The single visibility policy, applied to one audience picker:
 *
 * - no rules at all                    -> visible to everyone
 * - an Exclude rule the user matches   -> hidden, and Exclude always wins
 * - any Include rules present          -> visible only if the user matches one of them
 * - only Exclude rules the user misses -> visible
 *
 * Exclude taking precedence is deliberate: visibility must not depend on the order an editor
 * happened to add the rules in.
 */
const isVisibleForAudiences = (audiencePicker: Umbraco.AudiencePicker[] | undefined, audiences: Audiences): boolean => {
	if (!audiencePicker || audiencePicker.length === 0) return true;

	let hasInclusionRules = false;
	let isExplicitlyIncluded = false;

	for (const picker of audiencePicker) {
		const audienceProps = picker?.content?.properties;
		const matches = userHasAudience(audienceProps?.link?.properties?.audienceName, audiences);

		if (audienceProps?.Exclude) {
			if (matches) return false;
		} else {
			hasInclusionRules = true;
			if (matches) isExplicitlyIncluded = true;
		}
	}

	return hasInclusionRules ? isExplicitlyIncluded : true;
};

/**
 * "We could not find out" is not the same as "the user is in no audiences". Audiences are
 * unknown when the lookup failed, when it has not resolved, and when the API answered without
 * a usable result. In every one of those cases only content whose visibility does not depend
 * on audiences may be shown - otherwise every Exclude rule silently stops applying and gated
 * content leaks to everyone.
 */
const audiencesAreUnknown = (audiences: Audiences, audiencesUnknown: boolean): boolean => audiencesUnknown || !audiences;

/**
 * Custom hook to determine if content should be displayed based on audience picker.
 * @param audiencePicker The audience picker from Umbraco.
 * @returns A boolean indicating whether the content should be displayed.
 */
export const useShouldShowContent = (audiencePicker: Umbraco.AudiencePicker[]): boolean => {
	const profileContext = useContext(ProfileContext);

	if (!audiencePicker || audiencePicker.length === 0) return true;

	if (audiencesAreUnknown(profileContext?.audiences, !!profileContext?.audiencesFailed)) return false;

	return isVisibleForAudiences(audiencePicker, profileContext?.audiences);
};

export const filterItemsByAudience = <T>(
	items: T[],
	audiences: Audiences,
	getAudiencePicker?: (item: T) => Umbraco.AudiencePicker[] | undefined,
	audiencesUnknown = false,
): T[] => {
	// Callers map over the result, so never hand back undefined.
	if (!items) return [];

	const resolveAudiencePicker = (item: T): Umbraco.AudiencePicker[] | undefined =>
		getAudiencePicker
			? getAudiencePicker(item)
			: (item as { content?: { properties?: { audiencePicker?: Umbraco.AudiencePicker[] } } })?.content?.properties?.audiencePicker;

	if (audiencesAreUnknown(audiences, audiencesUnknown)) {
		return items.filter((item) => {
			const audiencePicker = resolveAudiencePicker(item);
			return !audiencePicker || audiencePicker.length === 0;
		});
	}

	return items.filter((item) => isVisibleForAudiences(resolveAudiencePicker(item), audiences));
};
